'use client';
import { Provider } from 'react-redux';
import { store } from '@lib/redux/store';
import { Suspense, useCallback, useEffect, useState } from 'react';
import { Provider as RollbarProvider, ErrorBoundary, useRollbar } from '@rollbar/react';
import { useInteractionGuard } from '@hooks/index';
import { SocketEventsProvider } from '@cable/socket-provider';
import { useAppSelector } from '@lib/redux/store';
import { redactRollbarEmailAttribution } from '@ui/analytics/posthog-url-redaction';
import { useEmailNotificationAttribution } from '@ui/hooks';
import { useVerifyEmailNotificationLandingMutation } from '@lib/redux/api/email-notification-attribution-api';
import { usePathname, useSearchParams } from 'next/navigation';
import { CreatorPostHogIdentityBoundary } from './creator-posthog-identity-boundary';
import { getCreatorMessageCenterIdentity } from '@lib/message-center-identity';

const rollbarConfig =
  process.env['NODE_ENV'] !== 'development'
    ? {
        accessToken: process.env['NEXT_PUBLIC_ROLLBAR_CLIENT_TOKEN'],
        captureUncaught: true,
        captureUnhandledRejections: true,
        transform: redactRollbarEmailAttribution,
        payload: {
          environment: process.env['NEXT_PUBLIC_ENVIRONMENT'],
          client: {
            javascript: {
              source_map_enabled: true,
              code_version: process.env.NEXT_PUBLIC_CODE_VERSION,
              guess_uncaught_frames: true,
            },
          },
        },
      }
    : {};

function GlobalSocketProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAppSelector((state) => state.app);
  const { authenticatedUserId, effectiveCreatorId } = getCreatorMessageCenterIdentity(user);

  if (!authenticatedUserId || !effectiveCreatorId) {
    return <>{children}</>;
  }

  const socketUserIds =
    authenticatedUserId === effectiveCreatorId
      ? authenticatedUserId
      : [authenticatedUserId, effectiveCreatorId];
  return <SocketEventsProvider userId={socketUserIds}>{children}</SocketEventsProvider>;
}

function EmailNotificationAttributionBoundary() {
  const userId = useAppSelector((state) => state.app.user?.id);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [verifyLandingMutation] = useVerifyEmailNotificationLandingMutation();
  const verifyLanding = useCallback(
    (request) => verifyLandingMutation(request).unwrap(),
    [verifyLandingMutation],
  );
  useEmailNotificationAttribution({
    authenticatedUserId: userId,
    pathname,
    searchParams: searchParams?.toString() || '',
    surface: 'creator_web',
    verifyLanding,
  });
  return null;
}

function InteractionGuardHandler({ children }: { children: React.ReactNode }) {
  const { isIntercepted, pendingFn, clearInterception } = useInteractionGuard();

  useEffect(() => {
    if (isIntercepted) {
      if (pendingFn) {
        pendingFn.fn(...pendingFn.args);
        clearInterception();
      }
    }
  }, [isIntercepted, pendingFn, clearInterception]);

  return <>{children}</>;
}

export function Providers({ children }: React.PropsWithChildren<{}>) {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  return (
    <RollbarProvider config={rollbarConfig}>
      <ErrorBoundary>
        <Provider store={store}>
          <CreatorPostHogIdentityBoundary />
          <Suspense fallback={null}>
            <EmailNotificationAttributionBoundary />
          </Suspense>
          <GlobalSocketProvider>
            {isMounted ? <InteractionGuardHandler>{children}</InteractionGuardHandler> : children}
          </GlobalSocketProvider>
        </Provider>
      </ErrorBoundary>
    </RollbarProvider>
  );
}

export function useLogErrorToRollbar(errorDetails) {
  const rollbar = useRollbar();
  useEffect(() => {
    if (errorDetails) {
      rollbar.error('Error Page Rendered', {
        ...errorDetails,
        stackTrace: errorDetails.error?.stack,
        componentStack: errorDetails.error?.componentStack,
      });
    }
  }, [errorDetails, rollbar]);
}
